import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { Plus, Route, Clock, MapPin } from 'lucide-react'
import api from '../../services/api'
import { todayInManaus } from '../../utils/dateManaus'
import Card from '../../components/ui/Card'
import Button from '../../components/ui/Button'
import DateFilterToggle from '../../components/ui/DateFilterToggle'

// Busca rotas
const fetchRoutes = async (filters, allDates) => {
  const { date, ...rest } = filters
  const params = allDates ? rest : { ...rest, date }
  const response = await api.get('/api/routes', { params })
  return response.data.data
}

// Status badge da rota
function RouteStatusBadge({ status }) {
  const statusConfig = {
    planejada: { label: 'Planejada', color: 'bg-gray-100 text-gray-700' },
    em_andamento: { label: 'Em andamento', color: 'bg-blue-100 text-blue-700' },
    finalizada: { label: 'Finalizada', color: 'bg-green-100 text-green-700' },
    cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-700' },
  }
  const config = statusConfig[status] || statusConfig.planejada
  return (
    <span className={`px-2 py-1 text-xs font-medium rounded-full ${config.color}`}>
      {config.label}
    </span>
  )
}

export default function RoutesList() {
  const [filters, setFilters] = useState({
    date: todayInManaus(),
  })
  const [allDates, setAllDates] = useState(false)

  const { data, isLoading } = useQuery({
    queryKey: ['routes', filters, allDates],
    queryFn: () => fetchRoutes(filters, allDates),
  })

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Rotas</h1>
          <p className="page-subtitle">Gerencie suas rotas de entrega otimizadas.</p>
        </div>
        <Link to="/routes/new">
          <Button>
            <Plus className="w-4 h-4" />
            Nova Rota
          </Button>
        </Link>
      </div>

      {/* Filtros */}
      <Card padding="sm">
        <DateFilterToggle
          value={filters.date}
          onChange={(date) => setFilters({ ...filters, date })}
          allDates={allDates}
          onToggleAllDates={setAllDates}
        />
      </Card>

      {/* Lista */}
      <div className="space-y-4">
        {isLoading ? (
          [...Array(3)].map((_, i) => (
            <div key={i} className="h-32 bg-gray-200 animate-pulse rounded-xl" />
          ))
        ) : data?.routes?.length === 0 ? (
          <Card>
            <div className="text-center py-12">
              <Route className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500 mb-4">Nenhuma rota encontrada para esta data</p>
              <Link to="/routes/new">
                <Button variant="outline" size="sm">
                  <Plus className="w-4 h-4" />
                  Criar primeira rota
                </Button>
              </Link>
            </div>
          </Card>
        ) : (
          data?.routes?.map((route) => (
            <Link key={route.id} to={`/routes/${route.id}`}>
              <Card className="hover:border-primary-300 hover:shadow-md transition-all cursor-pointer">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-primary-100 rounded-xl flex items-center justify-center">
                      <Route className="w-7 h-7 text-primary-600" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-gray-900">
                          Rota #{route.id.slice(-6).toUpperCase()}
                        </p>
                        <RouteStatusBadge status={route.status} />
                      </div>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <MapPin className="w-4 h-4" />
                          {route.deliveryCount || 0} entregas
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-4 h-4" />
                          {route.totalDuration ? `${Math.round(route.totalDuration / 60)} min` : '--'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">
                      {route.totalDistance 
                        ? `${(route.totalDistance / 1000).toFixed(1)} km`
                        : '--'
                      }
                    </p>
                  </div>
                </div>
              </Card>
            </Link>
          ))
        )}
      </div>
    </div>
  )
}
